import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, User, CheckCircle2 } from "lucide-react";
import { blogs } from "@/lib/blogData";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { SEOHead } from "@/components/SEOHead";

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogs.find(b => b.slug === slug);
  const [headings, setHeadings] = useState<{ id: string; text: string; level: number }[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    
    // Add ids to headings and build ToC
    const articleContent = document.getElementById("article-content");
    if (articleContent) {
      const elements = articleContent.querySelectorAll("h2, h3");
      const toc: { id: string; text: string; level: number }[] = [];
      
      elements.forEach((el, index) => {
        const id = el.id || `heading-${index}`;
        el.id = id;
        toc.push({
          id,
          text: el.textContent || "",
          level: parseInt(el.tagName.charAt(1))
        });
      });
      setHeadings(toc);
    }
  }, [post]);

  const scrollToHeading = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Post not found</h1>
          <Button asChild>
            <Link to="/blog">Back to Blog</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <article className="min-h-screen bg-background pt-32 pb-24">
      <SEOHead
        title={post.title}
        description={post.excerpt}
        canonical={`/blog/${post.slug}`}
        ogImage={post.image}
      />
      <header className="container mx-auto px-6 max-w-5xl mb-12">
        <Link to="/blog" className="inline-flex items-center text-primary hover:underline mb-8 font-medium">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to all articles
        </Link>
        <div className="max-w-3xl">
          <div className="text-sm font-bold text-primary mb-4 uppercase tracking-wider">{post.category}</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-6 leading-tight">
            {post.title}
          </h1>
          <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4" /> {post.date}</span>
            <span className="flex items-center gap-2"><User className="w-4 h-4" /> {post.author}</span>
          </div>
        </div>
        <div className="aspect-video md:aspect-[21/9] rounded-3xl overflow-hidden mb-12 border border-border/50 shadow-2xl relative">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
        </div>
      </header>

      <div className="container mx-auto px-6 max-w-5xl grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-12">
        <div className="min-w-0">
          <div 
            id="article-content"
            className="prose prose-invert prose-lg max-w-none prose-headings:font-bold prose-h2:text-3xl prose-h2:mt-12 prose-h3:text-xl prose-h3:mt-8 prose-p:text-muted-foreground prose-p:leading-relaxed prose-a:text-primary hover:prose-a:text-primary/80 prose-strong:text-foreground prose-ul:text-muted-foreground prose-li:marker:text-primary"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Conversion Block inside Blog Post */}
          <div className="mt-16 p-8 rounded-3xl bg-card border border-border/50 relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-[80px] -z-10" />
            <h3 className="text-2xl font-bold mb-4">Ready to implement this for your business?</h3>
            <p className="text-muted-foreground mb-6">
              Stop guessing and start growing. Let LRH Solutions build the exact systems and websites you need to capture more leads and close more deals.
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3 font-medium">
                <CheckCircle2 className="w-5 h-5 text-blue-500/80" /> Custom strategy tailored to your niche
              </li>
              <li className="flex items-center gap-3 font-medium">
                <CheckCircle2 className="w-5 h-5 text-blue-500/80" /> Done-for-you technical setup
              </li>
            </ul>
            <Button asChild size="lg" className="rounded-full font-bold relative group overflow-hidden bg-primary text-primary-foreground shadow-[0_0_20px_hsl(var(--primary)/0.3)] hover:shadow-[0_0_30px_hsl(var(--primary)/0.5)] transition-all duration-300">
              <Link to="/booking">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-shimmer" />
                <span className="relative z-10">Book Your Free Strategy Call</span>
              </Link>
            </Button>
          </div>
        </div>

        {/* Sidebar / Table of Contents */}
        <aside className="hidden lg:block relative">
          <div className="sticky top-32">
            <h4 className="font-bold mb-4 uppercase text-sm tracking-wider text-muted-foreground">Table of Contents</h4>
            {headings.length > 0 ? (
              <nav className="flex flex-col gap-3 border-l-2 border-border/50 pl-4">
                {headings.map(h => (
                  <button 
                    key={h.id} 
                    onClick={() => scrollToHeading(h.id)}
                    className={`text-left text-sm hover:text-primary transition-colors ${h.level === 3 ? 'ml-4 text-muted-foreground' : 'font-medium text-foreground'}`}
                  >
                    {h.text}
                  </button>
                ))}
              </nav>
            ) : (
              <p className="text-sm text-muted-foreground">No headings found.</p>
            )}
          </div>
        </aside>
      </div>
    </article>
  );
};

export default BlogPost;